import omitBy from "lodash/omitBy";
import { bigMath } from "../misc";
import {
	OrderItemCalculatedValues,
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionCalculatedValues,
	OrderItemOptionInputLike,
} from "../types";
import {
	$deepPopulateItemMetadata,
	$getItemMetadata,
	$getItemSubtotal,
	$getItemTotalRawPriceAddons,
	$getOptionMetadata,
	$getOptionSubtotal,
	$getOptionTotalRawPriceAddons,
	$mergeObj,
	BASE_SUBTOTAL_ADDONS,
	toRoundedNumber,
} from "./_internals";
import {
	$getOptionSubtotalAddons,
	$getSubtotalAddons,
} from "./subtotal-addons";
import { ORDER_ITEM_STATUS } from "~served/configs/enums";

export const calculateOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>,
) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	orderItems = orderItems.map((orderItem) => {
		const subtotal_addons = $getSubtotalAddons({
			item: orderItem,
			isCancelled: order.is_cancelled,
		});
		const { net_amount, gross_amount } = $getItemWithNetAndGrossAmount({
			...orderItem,
			subtotal_addons,
		});
		const cancelled_amount = 0;

		return {
			...orderItem,
			subtotal_addons,
			net_amount,
			gross_amount,
			cancelled_amount,
		};
	});
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const subtotal_addons = $getOptionSubtotalAddons({
				option: orderItemOption,
				item: orderItem,
				isCancelled: order.is_cancelled,
			});
			const { net_amount, gross_amount } = $getOptionWithNetAndGrossAmount({
				...orderItemOption,
				subtotal_addons,
			});
			const cancelled_amount = 0;

			return {
				...orderItemOption,
				subtotal_addons,
				net_amount,
				gross_amount,
				cancelled_amount,
			};
		}),
	}));

	let cancelled_items = (order.cancelled_items ?? []).map($getItemMetadata);
	cancelled_items = cancelled_items.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) =>
			$getOptionMetadata(orderItemOption, orderItem),
		),
	}));
	cancelled_items = cancelled_items.map((orderItem) => {
		const subtotal = 0;
		const subtotal_addons = $getSubtotalAddons({
			item: { ...orderItem, subtotal },
			isCancelled: true,
		});
		const net_amount = 0;
		const gross_amount = 0;
		const cancelled_amount = bigMath.sub(
			$getItemSubtotal({ ...orderItem, subtotal, net_amount, gross_amount }),
			$getItemTotalRawPriceAddons({
				...orderItem,
				subtotal,
				net_amount,
				gross_amount,
			}),
		); // to exclude any buried in amount so this is only `net` amount

		return {
			...orderItem,
			subtotal,
			subtotal_addons,
			net_amount,
			gross_amount,
			cancelled_amount,
		};
	});
	cancelled_items = cancelled_items.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const subtotal = 0;
			const subtotal_addons = $getOptionSubtotalAddons({
				option: { ...orderItemOption, subtotal },
				item: orderItem,
				isCancelled: true,
			});
			const net_amount = 0;
			const gross_amount = 0;
			const cancelled_amount = bigMath.sub(
				$getOptionSubtotal(
					{ ...orderItemOption, subtotal, net_amount, gross_amount },
					orderItem,
				),
				$getOptionTotalRawPriceAddons({
					...orderItemOption,
					subtotal,
					net_amount,
					gross_amount,
				}),
			); // to exclude any buried in amount so this is only `net` amount

			return {
				...orderItemOption,
				subtotal,
				subtotal_addons,
				net_amount,
				gross_amount,
				cancelled_amount,
			};
		}),
	}));

	const max_prepare_time = orderItems.reduce(
		(pre, cur) => (pre > cur.prep_time ? pre : cur.prep_time),
		0,
	);
	const items_count = orderItems.reduce((pre, cur) => pre + cur.quantity, 0);
	const items_count_by_types = orderItems.reduce(
		(pre, cur) => {
			if (!cur.type) return { ...pre, unknown: pre.unknown + cur.quantity };

			if (!pre[cur.type]) return { ...pre, [cur.type]: cur.quantity };

			return { ...pre, [cur.type]: pre[cur.type] + cur.quantity };
		},
		{ unknown: 0 },
	);
	const prepped_count = orderItems.reduce((pre, cur) => {
		return cur.status === ORDER_ITEM_STATUS.DELIVERED
			? pre + cur.quantity
			: pre;
	}, 0);

	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);
	const offer_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.offer.amount),
		0,
	);
	const discount_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.discount.amount),
		0,
	);
	const net_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.net_amount),
		0,
	);
	const vat_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.vat.amount),
		0,
	);
	const service_charge_amount = orderItems.reduce(
		(prev, cur) => bigMath.add(prev, cur.subtotal_addons.service_charge.amount),
		0,
	);
	const service_charge_bd_amount = orderItems.reduce(
		(prev, cur) =>
			bigMath.add(prev, cur.subtotal_addons.service_charge_bd.amount),
		0,
	);
	const tax_amount = orderItems.reduce(
		(prev, cur) => bigMath.add(prev, cur.subtotal_addons.tax?.amount ?? 0),
		0,
	);
	const card_surcharge_amount = orderItems.reduce(
		(prev, cur) => bigMath.add(prev, cur.subtotal_addons.card_surcharge.amount),
		0,
	);
	const adjustment_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.adjustment.amount),
		0,
	);
	const non_sales_credit_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.non_sales_credit_amount ?? 0),
		0,
	);
	const gross_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.gross_amount),
		0,
	);
	const cancelled_amount = cancelled_items.reduce(
		(pre, cur) => bigMath.add(pre, cur.cancelled_amount),
		0,
	);

	const $order = {
		...order,
		items: orderItems,
		cancelled_items,
		max_prepare_time,
		items_count,
		items_count_by_types,
		prepped_count,
		subtotal,
		offer_amount,
		discount_amount,
		net_amount,
		vat_amount,
		service_charge_amount,
		service_charge_bd_amount,
		tax_amount,
		card_surcharge_amount,
		adjustment_amount,
		grand_total: gross_amount,
		gross_amount,
		cancelled_amount,
		non_sales_credit_amount,
	};

	return {
		$order,
		$isAllItemsWithVat:
			!!orderItems.length &&
			orderItems.every((item) => !!item.subtotal_addons.vat.amount),
		$isSomeItemsWithVat:
			!!orderItems.length &&
			orderItems.some((item) => !!item.subtotal_addons.vat.amount),
		$isAllItemsWithServiceCharge:
			!!orderItems.length &&
			orderItems.every((item) => !!item.subtotal_addons.service_charge.amount),
		$isSomeItemsWithServiceCharge:
			!!orderItems.length &&
			orderItems.some((item) => !!item.subtotal_addons.service_charge.amount),
		$isAllItemsWithServiceChargeBd:
			!!orderItems.length &&
			orderItems.every(
				(item) => !!item.subtotal_addons.service_charge_bd.amount,
			),
		$isSomeItemsWithServiceChargeBd:
			!!orderItems.length &&
			orderItems.some(
				(item) => !!item.subtotal_addons.service_charge_bd.amount,
			),
		$isAllItemsWithTax:
			!!orderItems.length &&
			orderItems.every((item) => !!item.subtotal_addons.tax?.amount),
		$isSomeItemsWithTax:
			!!orderItems.length &&
			orderItems.some((item) => !!item.subtotal_addons.tax?.amount),
		$isAllItemsWithCardSurcharge:
			!!orderItems.length &&
			orderItems.every((item) => !!item.subtotal_addons.card_surcharge.amount),
		$isSomeItemsWithCardSurcharge:
			!!orderItems.length &&
			orderItems.some((item) => !!item.subtotal_addons.card_surcharge.amount),
		$roundedGrandTotal: toRoundedNumber(gross_amount),
		items: orderItems,
		cancelled_items,
		max_prepare_time,
		items_count,
		items_count_by_types,
		prepped_count,
		subtotal,
		offer_amount,
		discount_amount,
		net_amount,
		vat_amount,
		service_charge_amount,
		service_charge_bd_amount,
		tax_amount,
		card_surcharge_amount,
		adjustment_amount,
		grand_total: gross_amount,
		gross_amount,
		cancelled_amount,
		non_sales_credit_amount,
	};
};

export const $getItemWithNetAndGrossAmount = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	orderItem: OrderItemCalculatedValues<
		OrderItemInputLike<TOrderItem, TOrderItemOption>
	>,
) => {
	orderItem.subtotal_addons = {
		...BASE_SUBTOTAL_ADDONS,
		...omitBy(orderItem.subtotal_addons, (value) => value === null),
	};

	const isVatIncluded =
		!!orderItem.original_price_addons.vat.percentage &&
		!!orderItem.original_price_addons.vat.amount;
	const isServiceChargeIncluded =
		!!orderItem.original_price_addons.service_charge.percentage &&
		!!orderItem.original_price_addons.service_charge.amount;
	const isServiceChargeBdIncluded =
		!!orderItem.original_price_addons.service_charge_bd?.percentage &&
		!!orderItem.original_price_addons.service_charge_bd?.amount;

	let [gross_amount, net_amount] = [0, 0];

	if (isVatIncluded || isServiceChargeIncluded || isServiceChargeBdIncluded) {
		gross_amount = bigMath.add(
			bigMath.sub(
				orderItem.subtotal,
				orderItem.subtotal_addons.offer.amount,
				orderItem.subtotal_addons.discount.amount,
			),
			orderItem.subtotal_addons.adjustment.amount,
		);
		net_amount = bigMath.sub(
			gross_amount,
			orderItem.subtotal_addons.vat.amount,
			orderItem.subtotal_addons.service_charge.amount,
			orderItem.subtotal_addons.service_charge_bd.amount,
			orderItem.subtotal_addons.tax?.amount ?? 0,
			orderItem.subtotal_addons.card_surcharge.amount,
		);
	} else {
		net_amount = bigMath.sub(
			orderItem.subtotal,
			$getItemTotalRawPriceAddons(orderItem),
			orderItem.subtotal_addons.offer.amount,
			orderItem.subtotal_addons.discount.amount,
		);

		gross_amount = bigMath.add(
			net_amount,
			orderItem.subtotal_addons.vat.amount,
			orderItem.subtotal_addons.service_charge.amount,
			orderItem.subtotal_addons.service_charge_bd.amount,
			orderItem.subtotal_addons.tax?.amount ?? 0,
			orderItem.subtotal_addons.card_surcharge.amount,
			orderItem.subtotal_addons.adjustment.amount,
		);
	}

	net_amount = bigMath.sub(net_amount, orderItem.non_sales_credit_amount ?? 0);
	gross_amount = bigMath.sub(
		gross_amount,
		orderItem.non_sales_credit_amount ?? 0,
	);

	return {
		...orderItem,
		net_amount: Math.max(0, net_amount),
		gross_amount: Math.max(0, gross_amount),
	};
};

const $getOptionWithNetAndGrossAmount = <
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	orderItemOption: OrderItemOptionCalculatedValues<
		OrderItemOptionInputLike<TOrderItemOption>
	>,
) => {
	orderItemOption.subtotal_addons = {
		...BASE_SUBTOTAL_ADDONS,
		...omitBy(orderItemOption.subtotal_addons, (value) => value === null),
	};

	const isVatIncluded =
		!!orderItemOption.original_price_addons.vat.percentage &&
		!!orderItemOption.original_price_addons.vat.amount;
	const isServiceChargeIncluded =
		!!orderItemOption.original_price_addons.service_charge.percentage &&
		!!orderItemOption.original_price_addons.service_charge.amount;
	const isServiceChargeBdIncluded =
		!!orderItemOption.original_price_addons.service_charge_bd?.percentage &&
		!!orderItemOption.original_price_addons.service_charge_bd?.amount;

	let [gross_amount, net_amount] = [0, 0];

	if (isVatIncluded || isServiceChargeIncluded || isServiceChargeBdIncluded) {
		gross_amount = bigMath.add(
			bigMath.sub(
				orderItemOption.subtotal,
				orderItemOption.subtotal_addons.offer.amount,
				orderItemOption.subtotal_addons.discount.amount,
			),
			orderItemOption.subtotal_addons.adjustment.amount,
		);
		net_amount = bigMath.sub(
			gross_amount,
			orderItemOption.subtotal_addons.vat.amount,
			orderItemOption.subtotal_addons.service_charge.amount,
			orderItemOption.subtotal_addons.service_charge_bd.amount,
			orderItemOption.subtotal_addons.tax?.amount ?? 0,
			orderItemOption.subtotal_addons.card_surcharge.amount,
		);
	} else {
		net_amount = bigMath.sub(
			orderItemOption.subtotal,
			$getOptionTotalRawPriceAddons(orderItemOption),
			orderItemOption.subtotal_addons.offer.amount,
			orderItemOption.subtotal_addons.discount.amount,
		);

		gross_amount = bigMath.add(
			net_amount,
			orderItemOption.subtotal_addons.vat.amount,
			orderItemOption.subtotal_addons.service_charge.amount,
			orderItemOption.subtotal_addons.service_charge_bd.amount,
			orderItemOption.subtotal_addons.tax?.amount ?? 0,
			orderItemOption.subtotal_addons.card_surcharge.amount,
			orderItemOption.subtotal_addons.adjustment.amount,
		);
	}

	net_amount = bigMath.sub(
		net_amount,
		orderItemOption.non_sales_credit_amount ?? 0,
	);
	gross_amount = bigMath.sub(
		gross_amount,
		orderItemOption.non_sales_credit_amount ?? 0,
	);

	return {
		...orderItemOption,
		net_amount: Math.max(0, net_amount),
		gross_amount: Math.max(0, gross_amount),
	};
};

/**
 * paymentTypes should have the same currencies
 * this should be used before the payment is made
 * for after payment, use `isReceiptPaymentTypesValid`
 * */

export const $addOrderToOrderGroupCalculation = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	group,
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>,
) => {
	const {
		$order,
		$isAllItemsWithVat,
		$isAllItemsWithServiceCharge,
		$isAllItemsWithServiceChargeBd,
		$isSomeItemsWithVat,
		$isSomeItemsWithServiceCharge,
		$isSomeItemsWithServiceChargeBd,
		$isAllItemsWithTax,
		$isSomeItemsWithTax,
		$isAllItemsWithCardSurcharge,
		$isSomeItemsWithCardSurcharge,
		items,
		cancelled_items,
		max_prepare_time,
		items_count,
		items_count_by_types,
		prepped_count,
		subtotal,
		offer_amount,
		discount_amount,
		net_amount,
		vat_amount,
		service_charge_amount,
		service_charge_bd_amount,
		tax_amount,
		card_surcharge_amount,
		adjustment_amount,
		grand_total,
		gross_amount,
		cancelled_amount,
	} = calculateOrder(order);

	return {
		$orders: group.$orders.concat($order),
		$isAllItemsWithVat: group.$isAllItemsWithVat && $isAllItemsWithVat,
		$isSomeItemsWithVat: group.$isSomeItemsWithVat || $isSomeItemsWithVat,
		$isAllItemsWithServiceCharge:
			group.$isAllItemsWithServiceCharge && $isAllItemsWithServiceCharge,
		$isAllItemsWithServiceChargeBd:
			group.$isAllItemsWithServiceChargeBd && $isAllItemsWithServiceChargeBd,
		$isSomeItemsWithServiceCharge:
			group.$isSomeItemsWithServiceCharge || $isSomeItemsWithServiceCharge,
		$isSomeItemsWithServiceChargeBd:
			group.$isSomeItemsWithServiceChargeBd || $isSomeItemsWithServiceChargeBd,
		$isAllItemsWithTax: group.$isAllItemsWithTax && $isAllItemsWithTax,
		$isSomeItemsWithTax: group.$isSomeItemsWithTax || $isSomeItemsWithTax,
		$isAllItemsWithCardSurcharge:
			group.$isAllItemsWithCardSurcharge && $isAllItemsWithCardSurcharge,
		$isSomeItemsWithCardSurcharge:
			group.$isSomeItemsWithCardSurcharge || $isSomeItemsWithCardSurcharge,
		$roundedGrandTotal: toRoundedNumber(
			bigMath.add(group.grand_total, grand_total),
		),
		items: group.items.concat(items),
		cancelled_items: group.items.concat(cancelled_items),
		max_prepare_time:
			group.max_prepare_time < max_prepare_time
				? max_prepare_time
				: group.max_prepare_time,
		items_count: bigMath.add(group.items_count, items_count),
		items_count_by_types: $mergeObj(
			group.items_count_by_types,
			items_count_by_types,
		),
		prepped_count: bigMath.add(group.prepped_count, prepped_count),
		subtotal: bigMath.add(group.subtotal, subtotal),
		discount_amount: bigMath.add(group.discount_amount, discount_amount),
		offer_amount: bigMath.add(group.offer_amount, offer_amount),
		net_amount: bigMath.add(group.net_amount, net_amount),
		vat_amount: bigMath.add(group.vat_amount, vat_amount),
		service_charge_amount: bigMath.add(
			group.service_charge_amount,
			service_charge_amount,
		),
		service_charge_bd_amount: bigMath.add(
			group.service_charge_bd_amount,
			service_charge_bd_amount,
		),
		tax_amount: bigMath.add(group.tax_amount, tax_amount),
		card_surcharge_amount: bigMath.add(
			group.card_surcharge_amount,
			card_surcharge_amount,
		),
		adjustment_amount: bigMath.add(group.adjustment_amount, adjustment_amount),
		grand_total: bigMath.add(group.grand_total, grand_total),
		gross_amount: bigMath.add(group.gross_amount, gross_amount),
		cancelled_amount: bigMath.add(group.cancelled_amount, cancelled_amount),
	};
};
